import { endOfMonth, format, startOfMonth } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { useLazyGetCompaniesWithFiltersQuery } from 'store/api/companies';
import { useLazyGetSalesJournalQuery } from 'store/api/reports';
import { Company } from 'types/companies';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { dateStringToDateObject } from 'utils/parseDate';
import { buildVdRows, exportForeignSalesXlsx } from './foreignSalesExcel';

interface ForeignSalesFormValues {
    reportingMonth: string;
}

/** Picks the registry code of the default company (falling back to the first), or '' when none is set. */
const resolveBusinessId = (companies: Company[]): string => {
    const defaultCompany = companies.find((company) => company.isDefault) ?? companies[0];
    return defaultCompany?.regCode ?? '';
};

const ForeignSalesForm: FC = () => {
    const defaultValues = useMemo<ForeignSalesFormValues>(
        () => ({ reportingMonth: format(startOfMonth(new Date()), 'yyyy-MM-dd') }),
        []
    );

    const methods = useForm<ForeignSalesFormValues>({ defaultValues, mode: 'onChange' });
    const { control, handleSubmit } = methods;

    const [fetchSalesJournal, { isFetching: isFetchingJournal }] = useLazyGetSalesJournalQuery();
    const [fetchCompanies, { isFetching: isFetchingCompanies }] = useLazyGetCompaniesWithFiltersQuery();
    const [isExporting, setIsExporting] = useState(false);

    const onSubmit = useCallback(
        async (formValues: ForeignSalesFormValues) => {
            const pickedMonth = dateStringToDateObject(formValues.reportingMonth);
            const periodStartDate = format(startOfMonth(pickedMonth), 'yyyy-MM-dd');
            const periodEndDate = format(endOfMonth(pickedMonth), 'yyyy-MM-dd');
            const reportingMonth = format(pickedMonth, 'yyyy-MM');

            setIsExporting(true);
            try {
                const [response, companies] = await Promise.all([
                    fetchSalesJournal({ periodStartDate, periodEndDate }).unwrap(),
                    fetchCompanies({ pageSize: 200 }).unwrap(),
                ]);

                if (buildVdRows(response.invoices).length === 0) {
                    errorAlert(t('reports.foreign-sales.no-results'));
                    return;
                }

                const businessId = resolveBusinessId(companies.content);
                await exportForeignSalesXlsx(response, reportingMonth, businessId);
            } catch {
                errorAlert(t('reports.foreign-sales.export-error'));
            } finally {
                setIsExporting(false);
            }
        },
        [fetchSalesJournal, fetchCompanies]
    );

    const isBusy = isFetchingJournal || isFetchingCompanies || isExporting;

    const actions = useMemo(
        () => [
            <Button
                key="foreign-sales-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                disabled={isBusy}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="reports.foreign-sales.export" />
            </Button>,
        ],
        [handleSubmit, isBusy, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('reports.foreign-sales.title')} actions={actions} />
                <SectionLayout>
                    <Section title="reports.foreign-sales.section-title">
                        <div className="flex max-w-xl flex-col gap-4">
                            <div className="w-56">
                                <DateInput
                                    control={control}
                                    name="reportingMonth"
                                    label="reports.foreign-sales.reporting-month"
                                    granularity="month"
                                    fullWidth
                                    required
                                />
                            </div>
                        </div>
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

export default ForeignSalesForm;
